import { useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { useUserContext } from 'layers/content/v2/hooks/useUserContext'
import {
  operations as lmsOperations,
  selectors as lmsSelectors,
} from 'store/lmsManager'
import {
  operations as curriculumCourseOperations,
  selectors as curriculumCourseSelectors,
} from 'store/curriculumCourseManager'
import { transformCurriculumCourseTree } from 'layers/content/v3/transforms/transformCurriculumCourseTree'
import { transformAuthoredCourseTree } from 'layers/content/v3/transforms/transformAuthoredCourseTree'
import { isAuthoredCourseTreeEnabled } from 'utils/product/authoredCourseTree'
import { resolveSiteId } from 'hooks/useMarkCompleteV3/courseTreeHelpers'
import {
  buildCoursePointerForTreeFetch,
  resolveCourseCodeForTree,
} from 'utils/program/training'
import { getRegistrations } from 'utils/registrationHelpers'
import { isPreviewRoute } from 'utils/routing/routeHelpers'
import {
  extractClassIdFromSearch,
  getCurrentSearchString,
} from 'utils/routing/urlHelpers'

/**
 * Shared context hook that encapsulates common data fetching and resolution
 * logic for curriculum completion features. Provides instructor data, course
 * tree, registration data, and site ID resolution used by both
 * `useUnitLessonCompletion` and `useMarkCompleteV3`.
 *
 * @param {Object} options
 * @param {string|null} options.instructorDataKey - Top-level key in instructor/me response
 * @param {Object|null} options.coursePointer - Course pointer from curriculum-service
 * @param {string|null} options.contentGroupCode - Content group code (e.g. 'dwb')
 * @param {number|string|null} options.explicitSiteId - Explicit site ID
 * @param {boolean} options.skipInstructorFetch - Skip instructor data fetch for read-only contexts
 *
 * @returns {Object} Context data including instructor data, course tree, registrations, etc.
 */
export const useCurriculumCompletionContext = ({
  instructorDataKey,
  coursePointer,
  contentGroupCode,
  explicitSiteId,
  skipInstructorFetch = false,
}) => {
  const dispatch = useDispatch()
  const userContext = useUserContext()
  const location = useLocation()

  // --- Site ID resolution ---

  const resolvedSiteId = useMemo(
    () =>
      resolveSiteId({
        explicitSiteId,
        userContext,
        locationSearch: location?.search,
      }),
    [explicitSiteId, userContext, location?.search],
  )

  // --- Class ID from URL ---

  const classId = useMemo(() => {
    const search = getCurrentSearchString(location)
    return extractClassIdFromSearch(search)
  }, [location?.search])

  // --- Instructor data ---

  const isFetchingOrCreating = useSelector(
    state => !!state.lmsManager?.isFetchingOrCreatingProgramFlex,
  )

  const instructorData = useSelector(
    lmsSelectors.selectInstructorData(instructorDataKey),
  )

  const hasAttemptedInstructorFetch = useRef(false)

  useEffect(() => {
    if (skipInstructorFetch) return
    if (!instructorDataKey) return
    if (isFetchingOrCreating || instructorData) return
    if (isPreviewRoute()) return
    if (hasAttemptedInstructorFetch.current) return

    hasAttemptedInstructorFetch.current = true
    dispatch(lmsOperations.fetchInstructorProgramFlexGet())
  }, [
    dispatch,
    instructorDataKey,
    isFetchingOrCreating,
    instructorData,
    skipInstructorFetch,
  ])

  // --- Registration data ---

  const registrations = useMemo(() => getRegistrations(instructorData), [
    instructorData,
  ])

  const registrationCourseCode = useMemo(() => {
    if (!classId) return null
    return (
      registrations.find(reg => String(reg.registration_id) === String(classId))
        ?.course_name || null
    )
  }, [registrations, classId])

  // --- Course tree resolution ---

  const courseCodeForTree = useMemo(
    () =>
      resolveCourseCodeForTree({
        coursePointer,
        contentGroupCode,
        registrationCourseCode,
      }),
    [coursePointer, contentGroupCode, registrationCourseCode],
  )

  const coursePointerForTreeFetch = useMemo(
    () => buildCoursePointerForTreeFetch(coursePointer, courseCodeForTree),
    [coursePointer, courseCodeForTree],
  )

  const courseTreeFetchKey =
    courseCodeForTree && resolvedSiteId != null
      ? `${contentGroupCode || ''}|${courseCodeForTree}|${String(
          resolvedSiteId,
        )}`
      : null

  const lastAttemptedCourseTreeFetchKey = useRef(null)

  // Programs configured for the authored structure resolve leaf node ids from
  // the full authored tree, so completion works for nodes the caller's audience
  // would have filtered out of the audienced tree. Authored entries are cached
  // under their own key so they cannot collide with audienced ones.
  const useAuthoredTree = isAuthoredCourseTreeEnabled({ contentGroupCode })

  const courseTreeEntry = useSelector(
    curriculumCourseSelectors.selectCurriculumCourseTree(
      contentGroupCode,
      courseCodeForTree,
      resolvedSiteId,
      { authored: useAuthoredTree },
    ),
  )

  const courseTreeData = courseTreeEntry?.data
  const courseTreeLoading = courseTreeEntry?.loading
  const courseTreeError = courseTreeEntry?.error

  // --- Course tree fetching ---

  useEffect(() => {
    if (!instructorDataKey && !skipInstructorFetch) return
    if (!coursePointerForTreeFetch || !courseCodeForTree) return
    if (resolvedSiteId == null) return
    if (courseTreeData || courseTreeLoading || courseTreeError) return

    // Avoid duplicate fetches
    if (
      courseTreeFetchKey &&
      lastAttemptedCourseTreeFetchKey.current === courseTreeFetchKey
    ) {
      return
    }

    lastAttemptedCourseTreeFetchKey.current = courseTreeFetchKey

    const fetchCourseTree = useAuthoredTree
      ? curriculumCourseOperations.fetchAuthoredCourseTreeFromPointer
      : curriculumCourseOperations.fetchCourseTreeFromPointer

    dispatch(
      fetchCourseTree({
        coursePointer: coursePointerForTreeFetch,
        contentGroupCode,
        siteId: resolvedSiteId,
      }),
    )
  }, [
    coursePointerForTreeFetch,
    courseCodeForTree,
    courseTreeFetchKey,
    contentGroupCode,
    resolvedSiteId,
    instructorDataKey,
    skipInstructorFetch,
    useAuthoredTree,
    dispatch,
    courseTreeData,
    courseTreeLoading,
    courseTreeError,
  ])

  // --- Transformed course tree ---

  const transformedCourseTree = useMemo(() => {
    if (!courseTreeData) return null
    return useAuthoredTree
      ? transformAuthoredCourseTree(courseTreeData)
      : transformCurriculumCourseTree(courseTreeData)
  }, [courseTreeData, useAuthoredTree])

  // --- Loading state ---

  const hasCourseTreeDeps =
    !!instructorDataKey && !!courseCodeForTree && resolvedSiteId != null
  const isCourseTreeLoading =
    hasCourseTreeDeps && !courseTreeData && !courseTreeError

  const isInstructorDataLoading =
    !skipInstructorFetch && !!instructorDataKey && !instructorData

  const isLoading = isInstructorDataLoading || isCourseTreeLoading

  return {
    // Site and class context
    resolvedSiteId,
    classId,

    // Instructor data
    instructorData,
    isFetchingOrCreating,
    hasAttemptedInstructorFetch: hasAttemptedInstructorFetch.current,

    // Registration data
    registrations,
    registrationCourseCode,

    // Course tree data
    courseCodeForTree,
    coursePointerForTreeFetch,
    courseTreeEntry,
    courseTreeData,
    courseTreeLoading,
    courseTreeError,
    transformedCourseTree,

    // Loading state
    isLoading,
    isInstructorDataLoading,
    isCourseTreeLoading,
    hasCourseTreeDeps,
  }
}
